import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Drawer,
  Alert,
  Tooltip,
  Button,
} from "@mui/material";
import theme from "../../../config/theme/theme";
import { CompareArrows, Info, FileDownload } from "@mui/icons-material";
import { Cadet } from "../../../shared/types/scoreboard.types";
import { CalloutPair } from "../types";
import { Class5CalloutUtils } from "../utils/class5-callout.utils";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { ErrorBoundary } from "../../../components/ui/ErrorBoundary";
import { UI_CONSTANTS } from "../../../shared/constants";

interface Class5CalloutPairingsProps {
  scoreboardData: Cadet[];
  onCalloutComplete?: (updatedCadets: Cadet[]) => void;
  preloadedCalloutPairs?: CalloutPair[];
}

// Memoized table row component
const CalloutTableRow = React.memo(({ 
  pair, 
  cadets, 
  onCadetClick 
}: { 
  pair: CalloutPair; 
  cadets: Cadet[]; 
  onCadetClick: (cadet: Cadet) => void;
}) => {
  const cadet1 = cadets.find(c => c.primaryCadet === pair.cadet1.primaryCadet) || pair.cadet1;
  const cadet2 = cadets.find(c => c.primaryCadet === pair.cadet2.primaryCadet) || pair.cadet2;
  const winner = Class5CalloutUtils.determineWinner(cadet1, cadet2);

  const CadetCell = ({ cadet }: { cadet: Cadet }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {cadet.primaryCadet}
      <Tooltip title={`View ${cadet.primaryCadet}'s details`}>
        <IconButton size="small" onClick={() => onCadetClick(cadet)}>
          <Info fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const cellClass = (cadet: Cadet) => {
    const cadet1Total = Class5CalloutUtils.calculateC5RecSalesTotal(cadet1);
    const cadet2Total = Class5CalloutUtils.calculateC5RecSalesTotal(cadet2);
    
    if (cadet1Total > cadet2Total && cadet === cadet1) return "winner";
    if (cadet2Total > cadet1Total && cadet === cadet2) return "winner";
    return "";
  };
  
  const displayRank = (cadet: Cadet) => cadet.isDisqualified ? "DQ" : cadet.rank;

  return (
    <TableRow
      sx={{
        backgroundColor: theme.palette.grey[100],
        "& .winner": {
          backgroundColor: "#e8f5e9!important",
          color: "#2e7d32",
          fontWeight: "bold",
        },
      }}
    >
      <TableCell className={cellClass(cadet1)}>{displayRank(cadet1)}</TableCell>
      <TableCell className={cellClass(cadet1)}>
        <CadetCell cadet={cadet1} />
      </TableCell>
      <TableCell align="right" className={cellClass(cadet1)}>
        {Class5CalloutUtils.calculatePreviousPoints(cadet1).toFixed(2)}
      </TableCell>
      <TableCell align="right" className={cellClass(cadet1)}>
        {Class5CalloutUtils.calculateC5RecSalesTotal(cadet1).toFixed(2)}
      </TableCell>
      <TableCell align="center">
        <CompareArrows sx={{ transform: "rotate(90deg)" }} />
      </TableCell>
      <TableCell align="left" className={cellClass(cadet2)}>
        {Class5CalloutUtils.calculateC5RecSalesTotal(cadet2).toFixed(2)}
      </TableCell>
      <TableCell align="left" className={cellClass(cadet2)}>
        {Class5CalloutUtils.calculatePreviousPoints(cadet2).toFixed(2)}
      </TableCell>
      <TableCell className={cellClass(cadet2)}>
        <CadetCell cadet={cadet2} />
      </TableCell>
      <TableCell className={cellClass(cadet2)}>{displayRank(cadet2)}</TableCell>
    </TableRow>
  );
});

// Memoized cadet details component
const CadetDetails = React.memo(({ cadet }: { cadet: Cadet }) => {
  const previousTotal = Class5CalloutUtils.calculatePreviousPoints(cadet);
  const c5RecSalesTotal = Class5CalloutUtils.calculateC5RecSalesTotal(cadet);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {cadet.primaryCadet}
      </Typography>

      {cadet.isDisqualified && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This cadet has been disqualified
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" gutterBottom>
        SMD: {cadet.smd}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Rank: {cadet.rank === 999 ? 'DQ' : cadet.rank}
      </Typography>

      <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: "rgba(0,0,0,0.03)", borderRadius: 1 }}>
        <Typography variant="body2" gutterBottom>
          Previous Total (Qual + C1 + C2 + C3 + C4): {previousTotal.toFixed(2)}
        </Typography>
        <Typography variant="body2" gutterBottom>
          Class 5 Rec + Sales Total: {c5RecSalesTotal.toFixed(2)}
        </Typography>
        {cadet.c5 && cadet.c5.callout !== undefined && cadet.c5.callout !== 0 && (
          <Typography variant="body2" gutterBottom>
            Class 5 Callout: {cadet.c5.callout.toFixed(2)}
          </Typography>
        )}
        <Typography variant="body2" gutterBottom sx={{ fontWeight: "bold" }}>
          Current Total: {cadet.total.toFixed(2)}
        </Typography>
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
        Class 5 Performance:
      </Typography>
      
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        {[
          { label: "Recruit", value: cadet.c5 && cadet.c5.recruit ? cadet.c5.recruit : 0 },
          { label: "Sales", value: cadet.c5 && cadet.c5.sales ? cadet.c5.sales : 0 },
          { label: "Book", value: cadet.c5 && cadet.c5.book ? cadet.c5.book : 0 },
          { label: "Attendance", value: cadet.c5 && cadet.c5.attend ? cadet.c5.attend : 0 },
          { label: "Cycle Total", value: cadet.c5 && cadet.c5.total ? cadet.c5.total : 0, bold: true }
        ].map(({ label, value, bold }) => (
          <Box key={label}>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ fontWeight: bold ? "bold" : "normal" }}
            >
              {(value || 0).toFixed(2)}
            </Typography>
          </Box>
        ))}
      </Box>

      {cadet.c5 && cadet.c5.calloutNotes && (
        <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(25, 118, 210, 0.08)", borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Callout Result:
          </Typography>
          <Typography variant="body2">
            {cadet.c5.calloutNotes}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

export const Class5CalloutPairings: React.FC<Class5CalloutPairingsProps> = ({ 
  scoreboardData,
  onCalloutComplete,
  preloadedCalloutPairs 
}) => {
  const [selectedCadet, setSelectedCadet] = useState<Cadet | null>(null);
  const [calloutPairs, setCalloutPairs] = useState<CalloutPair[]>([]);
  const [cadets, setCadets] = useState<Cadet[]>(scoreboardData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Initialize callout pairs
  React.useEffect(() => {
    const initializePairs = async () => {
      if (preloadedCalloutPairs) {
        setCalloutPairs(preloadedCalloutPairs);
      } else {
        setIsLoading(true);
        try {
          const pairs = await Class5CalloutUtils.generateClass5CalloutPairs(scoreboardData);
          setCalloutPairs(pairs);
        } catch (err) {
          setError('Failed to generate Class 5 callout pairs');
          console.error('Error generating Class 5 callout pairs:', err);
        } finally {
          setIsLoading(false);
        }
      }
    };

    initializePairs();
  }, [scoreboardData, preloadedCalloutPairs]);

  const handleCadetClick = useCallback((cadet: Cadet) => {
    setSelectedCadet(cadet);
  }, []);

  const handleProcessCallout = useCallback(async () => {
    if (calloutPairs.length === 0) return;

    setIsLoading(true);
    try {
      const updatedCadets = Class5CalloutUtils.processClass5CalloutResults(cadets, calloutPairs);
      setCadets(updatedCadets);
      if (onCalloutComplete) {
        onCalloutComplete(updatedCadets);
      }
    } catch (err) {
      setError('Failed to process Class 5 callout results');
      console.error('Error processing Class 5 callout results:', err);
    } finally {
      setIsLoading(false);
    }
  }, [calloutPairs, cadets, onCalloutComplete]);

  const handleExportPairings = useCallback(async () => {
    try {
      await Class5CalloutUtils.exportClass5CalloutPairings(calloutPairs, cadets);
    } catch (error) {
      console.error('Failed to export Class 5 callout pairings:', error);
      alert('Failed to export Class 5 callout pairings. Please try again.');
    }
  }, [calloutPairs, cadets]);

  const handleExportResults = useCallback(async () => {
    try {
      await Class5CalloutUtils.exportPostClass5CalloutResults(cadets);
    } catch (error) {
      console.error('Failed to export Class 5 results:', error);
      alert('Failed to export Class 5 results. Please try again.');
    }
  }, [cadets]);

  const memoizedTableRows = useMemo(() => {
    if (!Array.isArray(calloutPairs)) {
      console.warn('calloutPairs is not an array:', calloutPairs);
      return [];
    }
    return calloutPairs.map((pair, index) => (
      <CalloutTableRow
        key={`${pair.cadet1.primaryCadet}-${pair.cadet2.primaryCadet}`}
        pair={pair}
        cadets={cadets}
        onCadetClick={handleCadetClick}
      />
    ));
  }, [calloutPairs, cadets, handleCadetClick]);

  if (isLoading) {
    return <LoadingSpinner message={UI_CONSTANTS.LOADING_MESSAGES.CALLOUT_DATA} />;
  }

  // if (error) {
  //   return (
  //     <ErrorBoundary 
  //       error={new Error(error)} 
  //       onRetry={() => {
  //         setError(null);
  //         setIsLoading(false);
  //       }} 
  //     />
  //   );
  // }

  return (
    <Box sx={{ width: isMobile ? "95vw" : "80%", margin: "auto", paddingTop: "20px" }}>
      <Typography
        variant="h3"
        sx={{
          fontFamily: "Georgia",
          marginBottom: "30px",
          fontSize: "clamp(1.7rem, 4vw, 4rem)",
        }}
        gutterBottom
      >
        Class 5 Callout
      </Typography>

      {/* <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={handleExportPairings}
          disabled={calloutPairs.length === 0}
        >
          Export Pairings
        </Button>

        <Button
          variant="outlined"
          startIcon={<FileDownload />}
          onClick={handleExportResults}
          disabled={cadets.length === 0}
        >
          Export Results
        </Button>
      </Box> */}

      {calloutPairs.length === 0 ? (
        <Alert severity="info">
          No Class 5 callout pairs available. Make sure the scoreboard data includes Class 5 performance data.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="right">Previous Total</TableCell>
                <TableCell align="right">Class 5 Rec + Sales</TableCell>
                <TableCell align="center">VS</TableCell>
                <TableCell align="left">Class 5 Rec + Sales</TableCell>
                <TableCell align="left">Previous Total</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Rank</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memoizedTableRows}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Drawer
        anchor={isMobile ? "bottom" : "right"}
        open={Boolean(selectedCadet)}
        onClose={() => setSelectedCadet(null)}
        PaperProps={{
          sx: {
            width: isMobile ? "100%" : 400,
            maxHeight: isMobile ? "80vh" : "100vh",
          },
        }}
      >
        {selectedCadet && <CadetDetails cadet={selectedCadet} />}
      </Drawer>
    </Box>
  );
};
